import { agentTemplateText } from "../i18n/template-view.js";
import { accountTalkRatesOfWire } from "./talk-rate-view.js";
import type { TalkRateView } from "./slice/talk-rate.js";
import { validatedDisplayView, extendDisplayView } from "../i18n/display-view.js";
import { portalText } from "../i18n/portal.js";
/**
 * Wire → view for the Agent surface's depth tabs (P11-01, P11-02, P11-03).
 *
 * It lives beside `conversation-view.ts` and for the same reason: the readers below turn an
 * `unknown` body into a typed view or **raise**, and never substitute a default. A missing
 * field on a documented response is a server defect, and a console that rendered `0` for it
 * would report the defect as a fact.
 *
 * TWO CONVERSIONS ARE DELIBERATELY DONE HERE RATHER THAN ON THE WIRE.
 *
 *   *Minutes.* The API answers in seconds, because that is what the meter counts. Rounding to
 *   minutes is a display decision, so it happens at the edge and nothing downstream inherits
 *   a figure that has already lost precision.
 *
 *   *Nothing else.* In particular **no percentage is computed from a percentage**: the API
 *   ships counts and the denominator, and `shareOf` below divides once, at the point of
 *   display, so two slices can never round to something that does not add up.
 */
import type { Money } from "@wetalk/domain";

import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "./decode.js";
import {
  channelLabelWordOf,
  formatWhen,
  readChannelLabel,
  readMedium,
  readMoney,
  readNullableText,
} from "./conversation-view.js";
import { latestBuildOfWire } from "./build-view.js";
import { answeringChoiceOfWire, specialDateListOfWire } from "./slice/voice-agent-edit.js";
import type { AnsweringChoice, ForwardFact, SpecialDateEntry } from "../routes/agents/new/opening-hours.js";
import type {
  AgentVersionChangeView,
  AgentVersionComparisonView,
  AgentVersionSummaryView,
  AnalyticsDailyPointView,
  AnalyticsHourView,
  AnalyticsMediumSliceView,
  AnalyticsRange,
  AnalyticsSliceView,
  ConversationFlagRaisedBy,
  ConversationFlagView,
  GapState,
  GapView,
  RankedGapView,
  TestCaseResultView,
  TestSuiteView,
  VoiceAgentAnalyticsView,
  VoiceAgentRecordingView,
  VoiceAgentState,
  VoiceAgentView,
} from "./gateway.js";

// ---------------------------------------------------------------------------------------
// Shared readers
// ---------------------------------------------------------------------------------------

const VOICE_AGENT_STATE: readonly VoiceAgentState[] = [
  "draft",
  "building",
  "live",
  "paused",
  "scheduled",
  "failed",
];

function readVoiceAgentState(value: unknown, path: string): VoiceAgentState {
  const raw = readText(value, path);
  const found = VOICE_AGENT_STATE.find((one) => one === raw);
  if (found === undefined) {
    throw new Error(
      portalText("shell", "the_api_answered_with_voiceagent_state_value_which_this_co_54c86854", { v0: String(raw) }),
    );
  }
  return found;
}

function readNullableWholeNumber(value: unknown, path: string): number | null {
  return value === null ? null : readWholeNumber(value, path);
}

/**
 * One slice's share of the whole, as a whole percentage.
 *
 * Returns `null` for a zero denominator. A share of nothing is not 0%, it is nothing, and the
 * screens print "—" rather than a bar of length zero that reads like a measurement.
 */
export function shareOf(part: number, whole: number): number | null {
  return whole === 0 ? null : Math.round((part / whole) * 100);
}

// ---------------------------------------------------------------------------------------
// VoiceAgent
// ---------------------------------------------------------------------------------------

export function voiceAgentOfWire(value: unknown, path: string): VoiceAgentView {
  const one = readRecord(value, path);
  const detail = DETAIL_KEY in one ? { detail: voiceAgentDetailOfWire(one, path) } : {};
  const billedSecondToday = readWholeNumber(one["billed_second_today"], `${path}.billed_second_today`);
  return {
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    displayName: readText(one["name"], `${path}.name`),
    agentTemplateCode: readText(one["agent_template_code"], `${path}.agent_template_code`),
    get agentTemplateName() { return agentTemplateText(readText(one["agent_template_name"], `${path}.agent_template_name`)); },
    state: readVoiceAgentState(one["state"], `${path}.state`),
    partyNumber: readNullableText(one["party_address"], `${path}.party_address`),
    // An em dash rather than an empty string: "there is no live version yet" is a fact, and
    // the header renders it where a label would go.
    liveAgentVersionLabel:
      readNullableText(one["live_version_label"], `${path}.live_version_label`) ?? "—",
    liveAgentVersionId: readNullableText(one["live_version_id"], `${path}.live_version_id`),
    liveVersionSeq: readWholeNumber(one["live_version_seq"], `${path}.live_version_seq`),
    languageCode: readArray(one["language_code"], `${path}.language_code`).map((code, index) =>
      readText(code, `${path}.language_code[${index}]`),
    ),
    recordingEnabled: readBoolean(one["recording_enabled"], `${path}.recording_enabled`),
    get successMetricLabel() { return agentTemplateText(readText(one["success_metric_label"], `${path}.success_metric_label`)); },
    // Seconds, not minutes: talk time is shown as minutes and seconds (`talkTimeText`), so
    // nothing is rounded before the screen.
    talkSecondToday: billedSecondToday,
    spendToday: readMoney(one["billed_amount_today"], `${path}.billed_amount_today`),
    spendThisMonth: readMoney(one["billed_amount_month"], `${path}.billed_amount_month`),
    createdAt: readText(one["created_at"], `${path}.created_at`),
    pausedBy: readPausedBy(one["paused_by"], `${path}.paused_by`),
    archived: readNullableText(one["archived_at"], `${path}.archived_at`) !== null,
    reachable: readBoolean(one["reachable"], `${path}.reachable`),
    latestBuild: latestBuildOfWire(one["latest_build"], `${path}.latest_build`),
    ...detail,
  };
}

// ── plan-015 AGC-UI: what only the VoiceAgent page draws (getVoiceAgent's VoiceAgentDetail) ──────────────────────

/*
 * OPTIONAL ON THE VIEW, AS LST'S `campaignDialling` IS: `getVoiceAgent` answers `VoiceAgentDetail` (plan-015 AGC-API),
 * the list rows and the lifecycle answers carry the plain `VoiceAgent`, and `api-gateway.ts` reads all three with
 * {@link voiceAgentOfWire}. So the detail is read whenever the body is one — recognised by its
 * `online_payment_unavailable` — and then EVERY detail field is required: a detail body missing one raises rather than
 * reading as "nothing to warn about". Absent on a list row, a lifecycle answer and the in-memory gateway's fixture.
 */
declare module "./gateway.js" {
  interface VoiceAgentView {
    /** `getVoiceAgent` only (plan-015): the voices, the live answering answers, the G3 warning and the forward facts. */
    readonly detail?: VoiceAgentDetailView;
  }
}

/** The key that marks a `VoiceAgentDetail` body. */
const DETAIL_KEY = "online_payment_unavailable";

/** The live version's answering answers (`VoiceAgentAnsweringAnswer`). */
export interface VoiceAgentAnsweringView {
  readonly timeZone: string;
  readonly specialDate: readonly SpecialDateEntry[];
  /** `null` when the live version's behaviours are not expressible as the choices. */
  readonly choice: AnsweringChoice | null;
  /** The hold a queued caller is really given, in seconds; `null` when callers do not queue (G8, owner answer 13). */
  readonly busyWaitSecond: number | null;
}

export interface VoiceAgentDetailView {
  /** Language code → its voice (plan-015 G1). */
  readonly voiceByLanguage: Readonly<Record<string, string>>;
  /** `null` before a version is live. */
  readonly answering: VoiceAgentAnsweringView | null;
  /** G3: the live version takes only online payment and the account cannot offer it now — the page warns. */
  readonly onlinePaymentUnavailable: boolean;
  /** Whether a forward may be chosen, how long it rings and how long a forwarded call may last. */
  readonly forward: ForwardFact;
  /**
   * plan-017 (OQ-1, design §29.11 row 12): the organization's rates of the kinds this VoiceAgent's enabled connections
   * are charged as, in `TalkRateKind` order; empty while nothing routes to it.
   */
  readonly talkRates: readonly TalkRateView[];
}

/**
 * The forward facts as `getVoiceAgent` and `GET /v1/templates` both serve them (plan-015 AGC-API, INT-B):
 * `forward_to_phone`, `forward_ring_second`, `forward_time_limit_second` on the object `one`.
 */
export function forwardFactOfWire(one: Readonly<Record<string, unknown>>, path: string): ForwardFact {
  const forwardToPhone = readText(one["forward_to_phone"], `${path}.forward_to_phone`);
  if (forwardToPhone !== "off" && forwardToPhone !== "on") {
    throw new Error(`Unknown forward_to_phone "${forwardToPhone}" at ${path}.forward_to_phone.`);
  }
  return {
    forwardToPhone,
    ringSecond: readWholeNumber(one["forward_ring_second"], `${path}.forward_ring_second`),
    timeLimitSecond: readWholeNumber(one["forward_time_limit_second"], `${path}.forward_time_limit_second`),
  };
}

function voiceAgentDetailOfWire(one: Readonly<Record<string, unknown>>, path: string): VoiceAgentDetailView {
  const voice = readRecord(one["voice_by_language"], `${path}.voice_by_language`);
  const rawAnswering = one["answering"];
  return {
    voiceByLanguage: Object.fromEntries(
      Object.entries(voice).map(([code, voiceId]) => [code, readText(voiceId, `${path}.voice_by_language.${code}`)]),
    ),
    answering: rawAnswering === null ? null : answeringOfWire(readRecord(rawAnswering, `${path}.answering`), `${path}.answering`),
    onlinePaymentUnavailable: readBoolean(one[DETAIL_KEY], `${path}.${DETAIL_KEY}`),
    forward: forwardFactOfWire(one, path),
    talkRates: accountTalkRatesOfWire(one["talk_rates"], `${path}.talk_rates`),
  };
}

function answeringOfWire(one: Readonly<Record<string, unknown>>, path: string): VoiceAgentAnsweringView {
  return {
    timeZone: readText(one["time_zone"], `${path}.time_zone`),
    specialDate: specialDateListOfWire(one["special_date"], `${path}.special_date`),
    choice: answeringChoiceOfWire(one, path),
    busyWaitSecond: readNullableWholeNumber(one["busy_wait_second"], `${path}.busy_wait_second`),
  };
}

// ── plan-015 LST: "Calling" on the Agents list (G15) ─────────────────────────────────────────

/*
 * OPTIONAL ON THE VIEW (the LIM precedent in `channel-view.ts`): plan-015 gives LST this file and not `gateway.ts`,
 * `api-gateway.ts` or `sandbox-gateway.ts`, so the list's field is merged onto `VoiceAgentView` here, optional.
 * Present (`true` or `false`) on every row `listVoiceAgents` answers once `apiGateway` maps the list with
 * {@link voiceAgentListItemOfWire}; absent from `getVoiceAgent`, which does not serve it, and from a gateway that
 * predates it (the sandbox's fixture). Absent draws the agent's own state, never "Calling".
 */
declare module "./gateway.js" {
  interface VoiceAgentView {
    /**
     * `campaign_dialling` (`GET /v1/agents` only): a campaign of this agent is in the running state now — not
     * scheduled, not paused. The list and the dashboard draw the design's "Calling" for it.
     */
    readonly campaignDialling?: boolean;
  }
}

/**
 * One row of `listVoiceAgents`: the VoiceAgent, plus `campaign_dialling`, which the list serves on every row and
 * this reader therefore requires — a row without it raises rather than reading as "not calling".
 */
export function voiceAgentListItemOfWire(value: unknown, path: string): VoiceAgentView {
  const one = readRecord(value, path);
  return extendDisplayView(voiceAgentOfWire(value, path), {
    campaignDialling: readBoolean(one["campaign_dialling"], `${path}.campaign_dialling`),
  });
}

/** `member` | `operator` | `null` (customer-portal review #106); anything else raises. */
function readPausedBy(value: unknown, path: string): "member" | "operator" | null {
  const raw = readNullableText(value, path);
  if (raw === null) return null;
  if (raw !== "member" && raw !== "operator") {
    throw new Error(`Unknown paused_by "${raw}" at ${path}.`);
  }
  return raw;
}

/** `updateVoiceAgent`'s answer (plan-003 RC-47). Raises on a missing field, never defaults. */
export function voiceAgentRecordingOfWire(value: unknown, path: string): VoiceAgentRecordingView {
  const one = readRecord(value, path);
  return {
    recordingEnabled: readBoolean(one["recording_enabled"], `${path}.recording_enabled`),
    liveVersionSeq: readWholeNumber(one["live_version_seq"], `${path}.live_version_seq`),
    changed: readBoolean(one["changed"], `${path}.changed`),
  };
}

// ---------------------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------------------

/**
 * How many whole days back each range reaches. `null` is "all", which sends no `since`.
 * "24h" is the one range that is not whole days: it reaches back exactly a day from now.
 */
export const ANALYTICS_RANGE_DAY: Readonly<Record<AnalyticsRange, number | null>> = {
  "24h": 1,
  "7d": 7,
  "14d": 14,
  "30d": 30,
  "90d": 90,
  all: null,
};

/**
 * The range control as a `since` instant.
 *
 * Cut in the **browser's** zone rather than sent as a word, for the same reason History's is:
 * "30 days" starts at a different instant for someone in Athens and someone in Lisbon, and
 * the browser is the one that knows which day the customer is looking at. The API then cuts
 * its day and hour buckets in the workspace's zone, which it names on the response, so the
 * chart's axis can say which zone it is drawn in.
 */
export function analyticsWindowOf(range: AnalyticsRange, at: Date): Record<string, string> {
  const days = ANALYTICS_RANGE_DAY[range];
  if (days === null) return {};
  if (range === "24h") return { since: new Date(at.getTime() - 86_400_000).toISOString() };
  // "14 days" is today and the thirteen before it (review #114): the API draws one point per
  // day from here, so a window reaching back `days` whole days would draw fifteen.
  const since = new Date(at.getFullYear(), at.getMonth(), at.getDate() - (days - 1));
  return { since: since.toISOString() };
}

const MEDIUM_LABEL: Readonly<Record<string, string>> = validatedDisplayView({
  get voice() { return portalText("shell", "voice_3091c844"); },
  get web_chat() { return portalText("shell", "web_chat_109d8596"); },
  whatsapp: "WhatsApp",
  get viber() { return portalText("shell", "viber_70774829"); },
});

function sliceOf(
  value: unknown,
  path: string,
  field: string,
  whenNull: string,
): AnalyticsSliceView {
  const one = readRecord(value, path);
  const raw = readNullableText(one[field], `${path}.${field}`);
  return {
    label: raw ?? whenNull,
    conversationCount: readWholeNumber(one["conversation_count"], `${path}.conversation_count`),
  };
}

export function analyticsOfWire(value: unknown, path: string): VoiceAgentAnalyticsView {
  const data = readRecord(value, path);
  const figure = readRecord(data["figure"], `${path}.figure`);
  const billedSecondTotal = readWholeNumber(
    figure["billed_second_total"],
    `${path}.figure.billed_second_total`,
  );

  return {
    timeZone: readText(data["time_zone"], `${path}.time_zone`),
    get successMetricLabel() { return agentTemplateText(readText(data["success_metric_label"], `${path}.success_metric_label`)); },
    conversationCount: readWholeNumber(
      figure["conversation_count"],
      `${path}.figure.conversation_count`,
    ),
    averageSeconds: readNullableWholeNumber(
      figure["average_billed_second"],
      `${path}.figure.average_billed_second`,
    ),
    talkSecond: billedSecondTotal,
    spend: readMoney(figure["billed_amount_total"], `${path}.figure.billed_amount_total`),
    listSpend: readMoney(figure["list_amount_total"], `${path}.figure.list_amount_total`),
    trialCovered:
      readWholeNumber(figure["charged_second_total"], `${path}.figure.charged_second_total`) <
      billedSecondTotal,
    outcomeReachedCount: readWholeNumber(
      figure["outcome_reached_count"],
      `${path}.figure.outcome_reached_count`,
    ),
    excludedTestCallCount: readWholeNumber(figure["excluded_test_call_count"], `${path}.figure.excluded_test_call_count`),
    priceUnavailableCount: readWholeNumber(figure["price_unavailable_count"], `${path}.figure.price_unavailable_count`),
    excludedUnansweredCount: readWholeNumber(
      figure["excluded_unanswered_count"],
      `${path}.figure.excluded_unanswered_count`,
    ),
    daily: readArray(data["daily"], `${path}.daily`).map(
      (row, index): AnalyticsDailyPointView => {
        const one = readRecord(row, `${path}.daily[${index}]`);
        return {
          day: readText(one["day"], `${path}.daily[${index}].day`),
          conversationCount: readWholeNumber(
            one["conversation_count"],
            `${path}.daily[${index}].conversation_count`,
          ),
        };
      },
    ),
    outcome: readArray(data["outcome"], `${path}.outcome`).map((row, index) =>
      sliceOf(row, `${path}.outcome[${index}]`, "outcome", portalText("shell", "no_outcome_reached_db328ce6")),
    ),
    language: readArray(data["language"], `${path}.language`).map((row, index) =>
      sliceOf(row, `${path}.language[${index}]`, "language_code", portalText("shell", "never_settled_bafa932b")),
    ),
    byMedium: readArray(data["by_medium"], `${path}.by_medium`).map(
      (row, index): AnalyticsMediumSliceView => {
        const at = `${path}.by_medium[${index}]`;
        const one = readRecord(row, at);
        const medium = readMedium(one["medium"], `${at}.medium`);
        const direction = readText(one["direction"], `${at}.direction`);
        if (direction !== "inbound" && direction !== "outbound") {
          throw new Error(`Unknown conversation direction "${direction}" at ${at}.`);
        }
        const channelLabel = readChannelLabel(one["channel_label"], `${at}.channel_label`);
        return {
          medium,
          direction,
          channelLabel,
          // A phone call keeps "Voice · inbound"; a website visitor reads as where they came from
          // (plan-004 WC-90: widget voice is `voice` too, and is not a phone call).
          label:
            channelLabel === "phone"
              ? `${MEDIUM_LABEL[medium] ?? medium} · ${direction}`
              : channelLabelWordOf(channelLabel),
          conversationCount: readWholeNumber(one["conversation_count"], `${at}.conversation_count`),
        };
      },
    ),
    hour: readArray(data["hour"], `${path}.hour`).map((row, index): AnalyticsHourView => {
      const at = `${path}.hour[${index}]`;
      const one = readRecord(row, at);
      return {
        hour: readWholeNumber(one["hour"], `${at}.hour`),
        conversationCount: readWholeNumber(one["conversation_count"], `${at}.conversation_count`),
      };
    }),
    agentVersion: readArray(data["agent_version"], `${path}.agent_version`).map(
      (row, index): AgentVersionComparisonView => {
        const at = `${path}.agent_version[${index}]`;
        const one = readRecord(row, at);
        return validatedDisplayView({
          agentVersionId: readText(one["agent_version_id"], `${at}.agent_version_id`),
          get label() { return readNullableText(one["agent_version_label"], `${at}.agent_version_label`) ?? "—"; },
          isLive: readBoolean(one["is_live"], `${at}.is_live`),
          conversationCount: readWholeNumber(one["conversation_count"], `${at}.conversation_count`),
          outcomeReachedCount: readWholeNumber(
            one["outcome_reached_count"],
            `${at}.outcome_reached_count`,
          ),
          averageSeconds: readNullableWholeNumber(
            one["average_billed_second"],
            `${at}.average_billed_second`,
          ),
        });
      },
    ),
    gap: readArray(data["gap"], `${path}.gap`).map((row, index): RankedGapView => {
      const at = `${path}.gap[${index}]`;
      const one = readRecord(row, at);
      return {
        gapId: readText(one["gap_id"], `${at}.gap_id`),
        question: readText(one["question_normalised"], `${at}.question_normalised`),
        callerCount: readWholeNumber(one["caller_count"], `${at}.caller_count`),
      };
    }),
  };
}

// ---------------------------------------------------------------------------------------
// Versions
// ---------------------------------------------------------------------------------------

const AGENT_VERSION_STATE: readonly AgentVersionSummaryView["state"][] = [
  "building",
  "ready",
  "failed",
  "archived",
];

const CHANGE_KIND: readonly AgentVersionChangeView["kind"][] = ["added", "removed", "changed"];

/** A field edit's before or after value as display text: text as is, nothing as "—", anything else as JSON. */
function fieldValueText(value: unknown): string {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "—";
  return JSON.stringify(value);
}

const PUBLISHER_KIND = ["person", "operator", "system"] as const;

/** `published_by_kind` + `published_by_name` (review #95); `null` while never published. */
function publishedByOfWire(
  one: Readonly<Record<string, unknown>>,
  path: string,
): AgentVersionSummaryView["publishedBy"] {
  const rawKind = readNullableText(one["published_by_kind"], `${path}.published_by_kind`);
  if (rawKind === null) return null;
  const kind = PUBLISHER_KIND.find((known) => known === rawKind);
  if (kind === undefined) throw new Error(`Unknown published_by_kind "${rawKind}" at ${path}.`);
  return { kind, name: readNullableText(one["published_by_name"], `${path}.published_by_name`) };
}

export function agentVersionOfWire(
  value: unknown,
  path: string,
  liveVersionId: string | null,
  at: Date,
): AgentVersionSummaryView {
  const one = readRecord(value, path);
  const rawState = readText(one["state"], `${path}.state`);
  const state = AGENT_VERSION_STATE.find((known) => known === rawState);
  if (state === undefined) {
    throw new Error(`Unknown agent_version state "${rawState}" at ${path}.`);
  }
  const agentVersionId = readText(one["agent_version_id"], `${path}.agent_version_id`);
  const publishedAt = readNullableText(one["published_at"], `${path}.published_at`);
  const authoredByPerson = readNullableText(
    one["authored_by_person_id"],
    `${path}.authored_by_person_id`,
  );
  const authoredByOperator = readNullableText(
    one["authored_by_operator_id"],
    `${path}.authored_by_operator_id`,
  );

  return validatedDisplayView({
    agentVersionId,
    get label() { return readText(one["label"], `${path}.label`); },
    ordinal: readWholeNumber(one["ordinal"], `${path}.ordinal`),
    state,
    get note() { return readNullableText(one["note"], `${path}.note`); },
    change: readArray(one["change_summary"], `${path}.change_summary`).map(
      (row, index): AgentVersionChangeView => {
        const site = `${path}.change_summary[${index}]`;
        const change = readRecord(row, site);
        // AMENDED 2026-10-11: a field edit made on the owner's request (v5 of a survey, 2026-10-08) records
        // `{ field, before, after }` instead of `{ kind, subject, detail }`; it reads as a change of that field.
        // Its values are whatever the field holds — text, a list, an object or nothing — shown as written.
        if (change["kind"] === undefined && change["field"] !== undefined) {
          return {
            kind: "changed",
            subject: readText(change["field"], `${site}.field`),
            detail: `${fieldValueText(change["before"])} → ${fieldValueText(change["after"])}`,
          };
        }
        const rawKind = readText(change["kind"], `${site}.kind`);
        const kind = CHANGE_KIND.find((known) => known === rawKind);
        if (kind === undefined) throw new Error(`Unknown change kind "${rawKind}" at ${site}.`);
        return {
          kind,
          subject: readText(change["subject"], `${site}.subject`),
          detail: readText(change["detail"], `${site}.detail`),
        };
      },
    ),
    languageCode: readArray(one["language_code"], `${path}.language_code`).map((code, index) =>
      readText(code, `${path}.language_code[${index}]`),
    ),
    greeting: readText(one["greeting"], `${path}.greeting`),
    channelUnitCeiling: readWholeNumber(
      one["concurrency_ceiling"],
      `${path}.concurrency_ceiling`,
    ),
    get publishedAtText() { return publishedAt === null ? null : formatWhen(publishedAt, at); },
    get createdAtText() { return formatWhen(readText(one["created_at"], `${path}.created_at`), at); },
    authoredBy:
      authoredByOperator !== null ? "operator" : authoredByPerson !== null ? "member" : "unknown",
    authorName: readNullableText(one["authored_by_name"], `${path}.authored_by_name`),
    publishedBy: publishedByOfWire(one, path),
    isLive: liveVersionId !== null && agentVersionId === liveVersionId,
  });
}

// ---------------------------------------------------------------------------------------
// The test set
// ---------------------------------------------------------------------------------------

export function testSuiteOfWire(value: unknown, path: string, at: Date): TestSuiteView {
  const one = readRecord(value, path);
  const latest = one["latest_run"];
  const run = latest === null ? null : readRecord(latest, `${path}.latest_run`);

  const resultByCase = new Map<string, Readonly<Record<string, unknown>>>();
  if (run !== null) {
    for (const [index, row] of readArray(run["result"], `${path}.latest_run.result`).entries()) {
      const site = `${path}.latest_run.result[${index}]`;
      const result = readRecord(row, site);
      resultByCase.set(readText(result["test_case_id"], `${site}.test_case_id`), result);
    }
  }

  return validatedDisplayView({
    testSuiteId: readText(one["test_suite_id"], `${path}.test_suite_id`),
    name: readText(one["name"], `${path}.name`),
    get description() { return readNullableText(one["description"], `${path}.description`); },
    testCase: readArray(one["test_case"], `${path}.test_case`).map(
      (row, index): TestCaseResultView => {
        const site = `${path}.test_case[${index}]`;
        const testCase = readRecord(row, site);
        const testCaseId = readText(testCase["test_case_id"], `${site}.test_case_id`);
        const result = resultByCase.get(testCaseId);
        return validatedDisplayView({
          testCaseId,
          utterance: readText(testCase["utterance"], `${site}.utterance`),
          expectation: readText(testCase["expectation"], `${site}.expectation`),
          // `null` means "this case was not in the last run", which is a third state and not
          // a pass. The card renders it as "not run", never as a tick.
          passed: result === undefined ? null : readBoolean(result["passed"], "result.passed"),
          get resultText() { return result === undefined ? null : readNullableText(result["result_text"], "result.result_text"); },
          transcriptExcerpt:
            result === undefined
              ? null
              : readNullableText(result["transcript_excerpt"], "result.transcript_excerpt"),
        });
      },
    ),
    lastRunAgainstLabel:
      run === null
        ? null
        : readNullableText(run["agent_version_label"], `${path}.latest_run.agent_version_label`),
    get lastRunAtText() { return run === null
        ? null
        : formatWhen(readText(run["started_at"], `${path}.latest_run.started_at`), at); },
    passedCount: run === null ? 0 : readWholeNumber(run["passed_count"], `${path}.latest_run.passed_count`),
    failedCount: run === null ? 0 : readWholeNumber(run["failed_count"], `${path}.latest_run.failed_count`),
  });
}

// ---------------------------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------------------------

const GAP_STATE: readonly GapState[] = ["open", "queued", "folded", "dismissed"];

export function gapOfWire(value: unknown, path: string, at: Date): GapView {
  const one = readRecord(value, path);
  const rawState = readText(one["state"], `${path}.state`);
  const state = GAP_STATE.find((known) => known === rawState);
  if (state === undefined) throw new Error(`Unknown gap state "${rawState}" at ${path}.`);
  return validatedDisplayView({
    gapId: readText(one["gap_id"], `${path}.gap_id`),
    question: readText(one["question_normalised"], `${path}.question_normalised`),
    representativeQuote: readNullableText(
      one["representative_quote"],
      `${path}.representative_quote`,
    ),
    callerCount: readWholeNumber(one["caller_count"], `${path}.caller_count`),
    get lastSeenText() { return formatWhen(readText(one["last_seen_at"], `${path}.last_seen_at`), at); },
    get answerText() { return readNullableText(one["answer_text"], `${path}.answer_text`); },
    answeredIntoLabel: readNullableText(
      one["answered_into_agent_version_label"],
      `${path}.answered_into_agent_version_label`,
    ),
    state,
    lastConversationId: readNullableText(one["last_conversation_id"], `${path}.last_conversation_id`),
  });
}

const RAISED_BY: readonly ConversationFlagRaisedBy[] = [
  "member",
  "operator",
  "automatic",
  "carrier",
];

export function conversationFlagOfWire(
  value: unknown,
  path: string,
  at: Date,
): ConversationFlagView {
  const one = readRecord(value, path);
  const rawRaisedBy = readText(one["raised_by_kind"], `${path}.raised_by_kind`);
  const raisedByKind = RAISED_BY.find((known) => known === rawRaisedBy);
  if (raisedByKind === undefined) {
    throw new Error(`Unknown raised_by_kind "${rawRaisedBy}" at ${path}.`);
  }
  const severity = readText(one["severity"], `${path}.severity`);
  if (severity !== "urgent" && severity !== "review") {
    throw new Error(`Unknown conversation_flag severity "${severity}" at ${path}.`);
  }
  return validatedDisplayView({
    conversationFlagId: readText(one["conversation_flag_id"], `${path}.conversation_flag_id`),
    conversationId: readText(one["conversation_id"], `${path}.conversation_id`),
    severity,
    reasonCode: readText(one["reason_code"], `${path}.reason_code`),
    excerpt: readNullableText(one["excerpt"], `${path}.excerpt`),
    raisedByKind,
    get raisedAtText() { return formatWhen(readText(one["raised_at"], `${path}.raised_at`), at); },
    handled: readNullableText(one["handled_at"], `${path}.handled_at`) !== null,
    partyAddress: readNullableText(one["party_address"], `${path}.party_address`),
    medium: readMedium(one["medium"], `${path}.medium`),
    billedSeconds: readNullableWholeNumber(one["billed_second"], `${path}.billed_second`),
  });
}

/** Re-exported so screens can price a slice without importing the domain's `Money` twice. */
export type { Money };
